// Reads and tidies the placeholder editor's contenteditable surface: its chips, serialisation to the
// raw value, and the trailing <br> end marker.

import { escapeLiteralBraces } from "./placeholder-tokens";

export const CHIP_CLASS = "placeholder-chip";

export function isChip(node: Node | null): boolean {
   return node?.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).classList.contains(CHIP_CLASS);
}

// Marks the trailing <br> that makes a final newline visible under `white-space: pre-wrap`.
const END_MARKER_ATTRIBUTE = "data-placeholder-end";

function isEndMarker(node: Node): boolean {
   return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute(END_MARKER_ATTRIBUTE);
}

/** An end marker or an empty text node: neither draws anything. */
function isInvisible(node: Node): boolean {
   return isEndMarker(node) || (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "") === "");
}

function isBlock(element: HTMLElement): boolean {
   return element.tagName === "DIV" || element.tagName === "P";
}

function isLastVisibleChild(node: Node): boolean {
   for (let next = node.nextSibling; next; next = next.nextSibling) {
      if (!isInvisible(next)) {
         return false;
      }
   }
   return true;
}

class SurfaceSerializer {
   readonly #surface: HTMLElement;
   #result = "";
   /** Literal text not yet escaped: a would-be token can span adjacent text nodes, so a run escapes as a whole. */
   #literal = "";
   /** Something visible (text, chip, line break) came before the current node. */
   #hasContent = false;
   /** A block opened or closed after visible content, so the next visible content starts a new line. */
   #pendingBreak = false;

   constructor(surface: HTMLElement) {
      this.#surface = surface;
   }

   serialize(): string {
      this.#writeChildren(this.#surface);
      this.#flushLiteral();
      return this.#result;
   }

   #writeChildren(parent: Node) {
      for (const node of Array.from(parent.childNodes)) {
         this.#writeNode(node);
      }
   }

   #writeNode(node: Node) {
      if (node.nodeType === Node.TEXT_NODE) {
         const text = node.textContent ?? "";
         if (text.length > 0) {
            this.#writeLiteral(text);
         }
         return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE || isEndMarker(node)) {
         return;
      }

      const element = node as HTMLElement;

      if (isChip(element)) {
         this.#writeChip(element.getAttribute("data-name") ?? "");
         return;
      }

      if (element.tagName === "BR") {
         // A <br> that ends a block (the surface is one too) only keeps that line open.
         const endsBlock = isLastVisibleChild(element) &&
            (element.parentNode === this.#surface || isBlock(element.parentElement!));
         this.#writeLiteral(endsBlock ? "" : "\n");
         return;
      }

      if (isBlock(element)) {
         // Chrome wraps each new line in a <div>, some browsers use <p>.
         this.#pendingBreak = this.#hasContent;
         this.#writeChildren(element);
         this.#pendingBreak = this.#hasContent;
         return;
      }

      this.#writeChildren(element);
   }

   /** Visible content follows: a line a block left pending starts here. */
   #openPendingLine() {
      if (this.#pendingBreak) {
         this.#literal += "\n";
         this.#pendingBreak = false;
      }
      this.#hasContent = true;
   }

   #writeLiteral(text: string) {
      this.#openPendingLine();
      this.#literal += text;
   }

   #writeChip(name: string) {
      this.#openPendingLine();
      this.#flushLiteral();
      this.#result += "{" + name + "}";
   }

   #flushLiteral() {
      this.#result += escapeLiteralBraces(this.#literal);
      this.#literal = "";
   }
}

/**
 * The canonical raw value of the surface: each chip -> `{name}`, each text run -> its text with
 * would-be tokens escaped, and block / line-break markup the browser may add -> the newlines a person
 * sees.
 */
export function serializeSurface(surface: HTMLElement): string {
   return new SurfaceSerializer(surface).serialize();
}

/**
 * Keeps one trailing <br> end marker exactly when the surface's last content is a text node ending
 * with a newline: `pre-wrap` draws no empty line for a final `\n`, so the caret could not sit on it.
 */
export function syncEndMarker(surface: HTMLElement) {
   let last = surface.lastChild;
   while (last && isInvisible(last)) {
      last = last.previousSibling;
   }

   const needed = last?.nodeType === Node.TEXT_NODE && (last.textContent ?? "").endsWith("\n");
   const markers = Array.from(surface.querySelectorAll(`[${END_MARKER_ATTRIBUTE}]`));

   if (needed && markers.length === 1 && markers[0] === surface.lastChild) {
      return;
   }

   for (const marker of markers) {
      marker.remove();
   }

   if (needed) {
      const marker = document.createElement("br");
      marker.setAttribute(END_MARKER_ATTRIBUTE, "");
      surface.appendChild(marker);
   }
}
